// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        isCartEmpty: isCartEmpty(cart),
    };
};

const mapDispatchToProps = {
    clearCurrentCart,
};

export interface EmptyCartAlertStyles {
    container?: InjectableCss;
    textStyles?: TypographyPresentationProps;
    alertWrapper?: InjectableCss;
}

export const emptyCartAlertStyles: EmptyCartAlertStyles = {};

const enum fields {
    alertMessage = "alertMessage",
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = emptyCartAlertStyles;

const EmptyCartAlert: React.FC<Props> = ({ fields: { alertMessage }, clearCurrentCart, isCartEmpty }) => {
    const clearCart = () => {
        clearCurrentCart();
    };

    if (isCartEmpty) {
        return null;
    }
    return (
        <StyledWrapper {...styles.alertWrapper}>
            <Page>
                <StyledWrapper {...styles.container}>
                    <Typography {...styles.textStyles}>{alertMessage && parse(alertMessage, parserOptions)}</Typography>
                    <Link onClick={clearCart}>{translate("Empty Shared Cart")}</Link>
                </StyledWrapper>
            </Page>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: XNGAGE_CUSTOM_WIDGET,
    displayName: "Empty Cart Alert",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.alertMessage,
            displayName: "Empty Cart Alert Message",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EmptyCartAlert),
    definition,
};

export default widgetModule;
